import * as React from 'react'
import { useState, useEffect } from 'react'
import clx from 'classnames'

// import { ChevronDownIcon } from '../icons/ChevronDown'
// import { CheckIcon } from '../icons/Check'
import { useClickOutside } from '@/app/hooks/useClickOutside'
import { ChevronDownIcon } from '@/app/features/icons/ChevronDown'
import { CheckIcon } from '@/app/features/icons/Check'

export type Option = {
  key?: string | number
  label: string
  value: string
  content?: React.ReactNode
  icon?: React.ElementType
}

type Props = {
  label?: string
  placeholder?: string
  icon?: React.ElementType
  options: Array<Option>
  className?: string
  value?: string | number | null
  error?: string | boolean
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement>
  handleChange?: (
    value: string,
    props: { name: string; option: Option },
  ) => void
  name?: string
  disabled?: boolean
  children?: React.ReactNode
  required?: boolean
}

export const FieldsetSelectSmall = ({
  label,
  value,
  options,
  name,
  placeholder = 'Wybierz...',
  icon: Icon = null,
  disabled,
  handleChange,
  onChange,
  error,
  className,
  required,
  children,
}: Props) => {
  const [storedValue, setStoredValue] = useState<string | number>(value)
  const [open, setOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const selected = options?.find((o) => o.value === value) || null

  const toggle = () => {
    if (!disabled) setOpen(!open)
  }

  const select = (option: Option) => {
    setOpen(false)
    setStoredValue(option.value)

    if (handleChange) handleChange(option.value, { name, option })

    if (onChange) {
      const event = {
        target: {
          name,
          value: option.value,
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>
      onChange(event)
    }
  }

  useEffect(() => {
    if (value !== undefined && value !== storedValue) {
      setStoredValue(value)
    }
  }, [value])

  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const listRef = React.useRef<HTMLUListElement>(null)

  useClickOutside({
    ref: dropdownRef,
    handler: () => setOpen(false),
  })

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key.length === 1 && /[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/.test(e.key)) {
        setOpen(true)
      }
      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocusedIndex((prev) => (prev + 1) % options.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusedIndex((prev) => (prev - 1 + options.length) % options.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (focusedIndex >= 0) select(options[focusedIndex])
    } else if (e.key.length === 1) {
      const letter = e.key.toLowerCase()
      const foundIndex = options.findIndex((o) =>
        o.label.toLowerCase().startsWith(letter),
      )
      if (foundIndex >= 0) {
        setFocusedIndex(foundIndex)
        const li = listRef.current?.children[foundIndex] as HTMLElement
        li?.scrollIntoView({ block: 'nearest' })
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  useEffect(() => {
    if (open && focusedIndex >= 0) {
      const li = listRef.current?.children[focusedIndex] as HTMLElement
      li?.scrollIntoView({ block: 'nearest' })
    }
  }, [focusedIndex, open])

  return (
    <div className="tw-w-full tw-max-w-[150px]">
      <input
        type="hidden"
        required={required}
        name={name}
        value={storedValue}
        readOnly
      />
      {label && (
        <label className="tw-group tw-mb-0 tw-px-1 tw-pb-0.5 tw-text-sm tw-font-medium">
          {required && <span className="tw-mr-0.5 tw-text-error">*</span>}
          {label}
        </label>
      )}
      <div
        className={clx('tw-relative tw-w-full', className)}
        ref={dropdownRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls="dropdown-list"
      >
        <button
          type="button"
          onClick={toggle}
          className={clx(
            'tw-flex tw-w-full tw-bg-white tw-max-w-[150px] tw-items-center tw-justify-between tw-rounded-md tw-border tw-px-2 tw-py-1 tw-text-left tw-transition-all focus:tw-ring-2 focus:tw-ring-focus-100 focus:tw-ring-offset-2',
            !open && 'hover:tw-opacity-80',
            {
              '[&:not(:focus)]:tw-border-error': error,
            },
          )}
        >
          <span className="tw-flex tw-items-center tw-text-xs tw-gap-1 tw-font-medium">
            {Icon && (
              <span className="tw-p-0.5">
                <Icon className="tw-h-4 tw-w-4" />
              </span>
            )}
            {selected ? (
              selected.label
            ) : (
              <span className="tw-text-xs tw-font-normal tw-text-input">
                {placeholder}
              </span>
            )}
          </span>
          <ChevronDownIcon
            className={`tw-h-4 tw-w-4 tw-transition-transform ${open ? 'tw-rotate-180' : ''}`}
          />
        </button>

        {open && (
          <ul
            ref={listRef}
            className="custom-scrollbar hide-scrollbar tw-absolute tw-z-10 tw-mt-2 tw-flex tw-max-h-[200px] tw-w-full tw-flex-col tw-gap-0.5 tw-overflow-y-auto tw-rounded-md tw-border tw-border-content-base tw-border-opacity-20 tw-bg-white tw-px-1.5 tw-py-1.5 tw-text-content-base tw-shadow-multi"
          >
            {options.map((option, idx) => (
              <li key={option.value}>
                <button
                  type="button"
                  onClick={() => select(option)}
                  className={clx(
                    'tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-rounded-[4px] tw-px-1 tw-py-2 tw-text-sm tw-text-content-base hover:tw-bg-base-300 hover:tw-bg-opacity-80 focus:tw-bg-base-300 focus:tw-bg-opacity-80 focus:tw-outline-none',
                    selected?.value === option.value &&
                      '!tw-bg-secondary tw-bg-opacity-80 tw-font-medium',
                    focusedIndex === idx && 'tw-bg-base-300 tw-bg-opacity-80',
                  )}
                >
                  <span className="tw-flex tw-items-center tw-gap-1">
                    {option.icon && (
                      <span className="tw-p-1">
                        <option.icon className="tw-h-3" />
                      </span>
                    )}
                    <span>
                      {option.content || option.label || option.value}
                    </span>
                  </span>
                  {selected?.value === option.value && (
                    <CheckIcon className="tw-h-5 tw-w-5 tw-text-focus-100" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        {name && <input type="hidden" name={name} value={value ?? ''} />}
      </div>
      {error && typeof error === 'string' && (
        <span className="tw-group-focus:tw-opacity-0 tw-text-xs tw-font-medium tw-text-error">
          {error}
        </span>
      )}
      <div>{children}</div>
    </div>
  )
}
